import { environment } from './environment';

// Interface for environment variables
interface EnvConfig {
  [key: string]: string;
}

// Function to parse .env file content
function parseEnvFile(content: string): EnvConfig {
  const config: EnvConfig = {};
  const lines = content.split('\n');
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Skip empty lines and comments
    if (!trimmedLine || trimmedLine.startsWith('#')) {
      continue;
    }
    
    // Parse KEY=VALUE format
    const equalIndex = trimmedLine.indexOf('=');
    if (equalIndex > 0) {
      const key = trimmedLine.substring(0, equalIndex).trim();
      let value = trimmedLine.substring(equalIndex + 1).trim();
      
      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      
      config[key] = value;
    }
  }
  
  return config;
}

// Function to load environment variables from .env file
export function loadEnvConfig(): EnvConfig {
  try {
    // In browser environment, we can't directly read files
    // So we'll use a different approach
    if (typeof window !== 'undefined') {
      // Check if environment variables are injected into the window object
      const windowEnv = (window as any).__ENV__;
      if (windowEnv) {
        return windowEnv;
      }
      
      // Fallback to default environment
      return {
        REACT_APP_API_URL: environment.apiUrl,
        REACT_APP_STRIPE_PUBLIC_KEY: environment.stripePublicKey,
        NODE_ENV: environment.production ? 'production' : 'development'
      };
    }
    
    // In Node.js environment (SSR), we can read the .env file
    if (typeof process !== 'undefined' && process.env) {
      return {
        REACT_APP_API_URL: process.env['REACT_APP_API_URL'] || environment.apiUrl,
        REACT_APP_STRIPE_PUBLIC_KEY: process.env['REACT_APP_STRIPE_PUBLIC_KEY'] || environment.stripePublicKey,
        NODE_ENV: process.env['NODE_ENV'] || (environment.production ? 'production' : 'development')
      };
    }
    
    // Fallback
    return {
      REACT_APP_API_URL: environment.apiUrl,
      REACT_APP_STRIPE_PUBLIC_KEY: environment.stripePublicKey,
      NODE_ENV: environment.production ? 'production' : 'development'
    };
    
  } catch (error) {
    console.warn('Failed to load environment configuration:', error);
    return {
      REACT_APP_API_URL: environment.apiUrl,
      REACT_APP_STRIPE_PUBLIC_KEY: environment.stripePublicKey,
      NODE_ENV: environment.production ? 'production' : 'development'
    };
  }
}

// Function to get environment variable with fallback
export function getEnvVar(key: string, fallback: string = ''): string {
  const envConfig = loadEnvConfig();
  return envConfig[key] || fallback;
}

// Export the loaded environment configuration
export const envConfig = loadEnvConfig();
